import { z } from 'zod';
/**
 * Activity event types for the host display feed
 */
export const ActivityEventType = z.enum(['action', 'achievement', 'milestone', 'system']);
/**
 * Activity event schema
 */
export const ActivityEvent = z.object({
    /** Unique event ID */
    id: z.string(),
    /** Player who triggered the event (optional for system events) */
    playerId: z.string().optional(),
    /** Player display name */
    playerName: z.string().optional(),
    /** Short action description */
    action: z.string(),
    /** Optional additional details */
    details: z.string().optional(),
    /** Event timestamp */
    timestamp: z.number(),
    /** Event type for styling */
    type: ActivityEventType,
    /** Optional point value for scoring events */
    points: z.number().optional(),
    /** Highlight level for special styling */
    highlight: z.enum(['normal', 'important', 'epic']).default('normal'),
});
/**
 * Activity update message (server -> host)
 */
export const ActivityUpdate = z.object({
    type: z.literal('ACTIVITY_UPDATE'),
    event: ActivityEvent,
});
/**
 * Helper to create activity events
 */
export function createActivityEvent(action, options = {}) {
    return {
        id: `activity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        action,
        timestamp: Date.now(),
        type: options.type ?? 'action',
        highlight: options.highlight ?? 'normal',
        ...options,
    };
}
/**
 * Game-specific activity message generators
 */
export const ActivityMessages = {
    trivia: {
        correct: (name, points) => createActivityEvent(`${name} answered correctly!`, {
            playerName: name,
            type: 'action',
            points,
            highlight: points >= 200 ? 'important' : 'normal',
        }),
        incorrect: (name) => createActivityEvent(`${name} answered incorrectly`, {
            playerName: name,
            type: 'action',
        }),
        timeout: (name) => createActivityEvent(`${name} ran out of time`, {
            playerName: name,
            type: 'action',
        }),
        streak: (name, streak) => createActivityEvent(`${name} is on a ${streak}-answer streak!`, {
            playerName: name,
            type: 'achievement',
            highlight: streak >= 5 ? 'epic' : 'important',
        }),
    },
    chess: {
        move: (name, piece, to) => createActivityEvent(`${name} moved ${piece} to ${to}`, {
            playerName: name,
            type: 'action',
        }),
        capture: (name, piece, captured) => createActivityEvent(`${name} captured ${captured} with ${piece}!`, {
            playerName: name,
            type: 'action',
            highlight: 'important',
        }),
        castling: (name, side) => createActivityEvent(`${name} castled ${side}!`, {
            playerName: name,
            type: 'action',
            highlight: 'important',
        }),
        promotion: (name, piece) => createActivityEvent(`${name} promoted pawn to ${piece}!`, {
            playerName: name,
            type: 'achievement',
            highlight: 'epic',
        }),
        check: (name) => createActivityEvent(`${name} put opponent in check!`, {
            playerName: name,
            type: 'action',
            highlight: 'important',
        }),
        checkmate: (name) => createActivityEvent(`Checkmate! ${name} wins!`, {
            playerName: name,
            type: 'milestone',
            highlight: 'epic',
        }),
        stalemate: () => createActivityEvent('Stalemate! The game is a draw.', {
            type: 'milestone',
            highlight: 'important',
        }),
    },
    poker: {
        bet: (name, amount) => createActivityEvent(`${name} bet $${amount}`, {
            playerName: name,
            type: 'action',
        }),
        raise: (name, amount) => createActivityEvent(`${name} raised to $${amount}`, {
            playerName: name,
            type: 'action',
            highlight: amount >= 500 ? 'important' : 'normal',
        }),
        call: (name) => createActivityEvent(`${name} called`, {
            playerName: name,
            type: 'action',
        }),
        fold: (name) => createActivityEvent(`${name} folded`, {
            playerName: name,
            type: 'action',
        }),
        allIn: (name) => createActivityEvent(`${name} went all-in!`, {
            playerName: name,
            type: 'action',
            highlight: 'epic',
        }),
        win: (name, amount) => createActivityEvent(`${name} wins $${amount} pot!`, {
            playerName: name,
            type: 'milestone',
            points: amount,
            highlight: 'epic',
        }),
    },
    checkers: {
        move: (name) => createActivityEvent(`${name} moved a piece`, {
            playerName: name,
            type: 'action',
        }),
        jump: (name, count) => createActivityEvent(count > 1 ? `${name} jumped ${count} pieces!` : `${name} jumped a piece!`, {
            playerName: name,
            type: 'action',
            highlight: count > 1 ? 'important' : 'normal',
        }),
        king: (name) => createActivityEvent(`${name} crowned a King!`, {
            playerName: name,
            type: 'achievement',
            highlight: 'important',
        }),
        win: (name) => createActivityEvent(`${name} wins the game!`, {
            playerName: name,
            type: 'milestone',
            highlight: 'epic',
        }),
    },
    tictactoe: {
        move: (name, symbol) => createActivityEvent(`${name} placed ${symbol}`, {
            playerName: name,
            type: 'action',
        }),
        win: (name) => createActivityEvent(`${name} wins!`, {
            playerName: name,
            type: 'milestone',
            highlight: 'epic',
        }),
        draw: () => createActivityEvent("It's a draw!", {
            type: 'milestone',
            highlight: 'important',
        }),
    },
    system: {
        playerJoined: (name) => createActivityEvent(`${name} joined the game`, {
            playerName: name,
            type: 'system',
        }),
        playerLeft: (name) => createActivityEvent(`${name} left the game`, {
            playerName: name,
            type: 'system',
        }),
        gameStarted: (gameName) => createActivityEvent(`${gameName} has started!`, {
            type: 'system',
            highlight: 'important',
        }),
    },
};
//# sourceMappingURL=activity.js.map